import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import React from "react";
import { classNames } from "primereact/utils";
import { Checkbox } from "primereact/checkbox";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  editProfile,
  getAccess,
  getPermissions,
  getProfileById,
  saveProfil,
} from "@core/actions/usersActions.ts";
import ExternalAgentForm from "@modules/setting/userManagement/profile/externalAgentForm.tsx";
import {
  access,
  loadingAccess,
  loadingEditProfil,
  loadingGetProfileId,
  loadingPermission,
  loadingSaveProfile,
  permission,
  profileById,
  selectedaccess,
  setSelectedAccess,
} from "@core/slice/UsersSlice.ts";
import { ErrorApi, FetchStatusType } from "@core/modeles/model.ts";
import { useNavigate, useParams } from "react-router-dom";
import Message from "@shared/message/Message.tsx";
import styles from "@modules/setting/userManagement/addingUser/addingUser.module.css";
import Spinner from "@shared/Spinner.tsx";
import { RadioButton } from "primereact/radiobutton";
import { Dropdown } from "primereact/dropdown";
import { PartenerModel, ProfilPutDTO } from "../profil.model.ts";
import { TypeProfil } from "@modules/plaintes/plaintes.model.ts";

const AddingProfile = () => {
  const navigate = useNavigate();
  const params = useParams();
  const idProfile = params.id;
  const isEditMode = !!idProfile;
  const dispatch = useAppDispatch();
  const listPermission = useAppSelector(permission);
  const listAccess = useAppSelector(access);
  const loadPermission = useAppSelector(loadingPermission);
  const loadAccess = useAppSelector(loadingAccess);
  const loadingProfilById = useAppSelector(loadingGetProfileId);
  const profilById = useAppSelector(profileById);
  const selectedAccess = useAppSelector(selectedaccess);
  const loading = useAppSelector(loadingSaveProfile);
  const loadingEdit = useAppSelector(loadingEditProfil);
  const [file, setFile] = React.useState<File | null>(null);
  const [selectedPermission, setSelectedPermission] = React.useState<string[]>(
    []
  );
  const [internalCategory, setInternalCategory] = React.useState<string>("");
  const [isExternalAgent, setIsExternalAgent] = React.useState<boolean>(false);
  const [partner, setPartner] = React.useState<PartenerModel>({
    acronym: "",
    concernedBranches: [],
    generalEmailAddress: "",
    interventionDomains: "",
    // interventionZone: "", // Champ désactivé (ne pas utiliser)
    legalStatuses: [],
    mainPhoneNumber: "",
    otherConcernedBranches: "",
    otherLegalStatus: "",
    otherPartnershipType: "",
    otherReferenceDocument: "",
    otherReportingChannel: "",
    organizationName: "",
    partnershipTypes: [],
    physicalOrPostalAddress: "",
    referenceDocuments: [],
    reportingChannels: [],
    rolesAndResponsibilities: "",
    website: "",
  });
  const [open, setOpen] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });
  const [formData, setFormData] = React.useState<ProfilPutDTO>({
    name: "",
    profileId: "",
    description: "",
    accesses: [],
    permissions: [],
    partner: null,
  });

  const [errors, setErrors] = React.useState({
    name: "",
    description: "",
    accesses: "",
    permissions: "",
    access: "",
    internalCategory: "",
    partner: "",
    admin: "",
  });
  React.useEffect(() => {
    
    if (
      idProfile &&
      profilById &&
      profilById.permissions &&
      profilById.accesses
    ) {
      setSelectedPermission([...profilById.permissions.map((p) => p.id!)]);
      dispatch(setSelectedAccess([...profilById.accesses.map((p) => +p.id)]));
      setFormData({
        name: profilById.name,
        description: profilById.description,
        accesses: profilById.accesses.map((a) => +a.id),
        permissions: profilById.permissions.map((p) => p.id!),
        profileId: profilById.id,
        partner: profilById.partner || null,
      });
      setPartner(
        profilById.partner ?? {
          acronym: "",
          concernedBranches: [],
          generalEmailAddress: "",
          interventionDomains: "",
          // interventionZone: "", // Champ désactivé (ne pas utiliser)
          legalStatuses: [],
          mainPhoneNumber: "",
          otherConcernedBranches: "",
          otherLegalStatus: "",
          otherPartnershipType: "",
          otherReferenceDocument: "",
          otherReportingChannel: "",
          organizationName: "",
          partnershipTypes: [],
          physicalOrPostalAddress: "",
          referenceDocuments: [],
          reportingChannels: [],
          rolesAndResponsibilities: "",
          website: "",
        }
      );
      if (profilById.type === TypeProfil.EXTERNAL) {
        setIsExternalAgent(true);
      } else {
        setIsExternalAgent(false);
      }
      const internalAccessSelected = profilById.accesses.find(
        (a) => a.internalAgent
      );
      if (internalAccessSelected) {
        setInternalCategory(internalAccessSelected.id.toString());
      } else {
        setInternalCategory("");
      }
    } else if (!idProfile) {
      setSelectedPermission([]);
      dispatch(setSelectedAccess([]));
      setFormData({
        name: "",
        profileId: "",
        description: "",
        accesses: [],
        permissions: [],
        partner: null,
      });
      setPartner({
        acronym: "",
        concernedBranches: [],
        generalEmailAddress: "",
        interventionDomains: "",
        // interventionZone: "", // Champ désactivé (ne pas utiliser)
        legalStatuses: [],
        mainPhoneNumber: "",
        otherConcernedBranches: "",
        otherLegalStatus: "",
        otherPartnershipType: "",
        otherReferenceDocument: "",
        otherReportingChannel: "",
        organizationName: "",
        partnershipTypes: [],
        physicalOrPostalAddress: "",
        referenceDocuments: [],
        reportingChannels: [],
        rolesAndResponsibilities: "",
        website: "",
      });
      setIsExternalAgent(false);
      setInternalCategory("");
    }
  }, [idProfile, profilById, dispatch]);

  React.useEffect(() => {
    dispatch(getPermissions());
    dispatch(getAccess());
  }, [dispatch]);

  React.useEffect(() => {
    if (idProfile) dispatch(getProfileById(idProfile));
  }, [dispatch, idProfile]);

  React.useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      partner: partner,
    }));
  }, [partner]);

  const showSuccessMessage = () => {
    setMessageInfo({
      severity: "success",
      summary: "Succès",
      detail: idProfile
        ? "Modification groupe réussie!"
        : "Enregistrement groupe réussi",
    });
    setOpen(true);
  };

  const showErrorMessage = (error: ErrorApi) => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: error.message,
    });
    setOpen(true);
  };

  const isExternalSelected =
    listAccess
      .find((val) => +val.id === selectedAccess[0])
      ?.name.toLowerCase() === "agent externe";

  // Fonction pour nettoyer le partner en supprimant les champs vides
  const cleanPartnerData = (partnerData: PartenerModel): any => {

    const cleaned: any = { ...partnerData };
    
    // Supprimer les champs "other..." s'ils sont vides
    if (!cleaned.otherLegalStatus || cleaned.otherLegalStatus.trim() === '') {
      delete cleaned.otherLegalStatus;
    }
    if (!cleaned.otherConcernedBranches || cleaned.otherConcernedBranches.trim() === '') {
      delete cleaned.otherConcernedBranches;
    }
    if (!cleaned.otherPartnershipType || cleaned.otherPartnershipType.trim() === '') {
      delete cleaned.otherPartnershipType;
    }
    if (!cleaned.otherReferenceDocument || cleaned.otherReferenceDocument.trim() === '') {
      delete cleaned.otherReferenceDocument;
    }
    if (!cleaned.otherReportingChannel || cleaned.otherReportingChannel.trim() === '') {
      delete cleaned.otherReportingChannel;
    }
    
    return cleaned;
  };

  const handleSaveProfil = () => {
    let formErrors = { ...errors };
    if (formData.name.trim() === "") {
      formErrors.name = "Veuillez remplir le champ nom";
    } else formErrors.name = "";

    if (formData.description.trim() === "") {
      formErrors.description = "Veuillez remplir le champ description";
    } else formErrors.description = "";

    if (formData.permissions.length === 0) {
      formErrors.permissions = "Veuillez choisir au moins une permission";
    } else formErrors.permissions = "";

    if (formData.accesses.length === 0) {
      formErrors.accesses = "Veuillez choisir au moins un accès";
    } else formErrors.accesses = "";
    if (selectedAccess[0] === 0 && !internalCategory) {
      formErrors.internalCategory =
        "Veuillez choisir au moins un agent interne.";
    } else formErrors.internalCategory = "";
    if (selectedAccess[0] === 100) {
      formErrors.admin = "Veuillez choisir au moins un admin.";
    } else formErrors.admin = "";
    setErrors(formErrors);

    const hasErrors = Object.values(formErrors).some((error) => error !== "");

    if (!hasErrors) {
      setOpen(false);
      
      // Nettoyer le partner en supprimant les champs vides
      const cleanedPartner = isExternalSelected ? cleanPartnerData(partner) : null;
      
      const dataToSave = {
        name: formData.name,
        description: formData.description,
        permissions: selectedPermission,
        accesses: selectedAccess,
        partner: cleanedPartner,
      };

      if (!idProfile) {
        dispatch(
          saveProfil({
            filter: {
              data: dataToSave,
              file: file,
            },
            onSuccess: () => {
              showSuccessMessage();

              setTimeout(() => {
                setOpen(false);
                navigate("/setting/group");
              }, 2000);
            },
            onError: (error) => {
              showErrorMessage(error);
            },
          })
        );
      } else {
        const cleanedFormDataPartner = formData.partner ? cleanPartnerData(formData.partner) : null;
        dispatch(
          editProfile({
            filter: {
              id: idProfile,
              profileId: formData.profileId,
              data: {
                name: formData.name,
                description: formData.description,
                permissions: formData.permissions,
                accesses: formData.accesses,
                partner: cleanedFormDataPartner,
                uploadedFile: formData.uploadedFile ?? null,
              },
              file: formData.uploadedFile?.content ?? null,
            },
            onSuccess: () => {
              showSuccessMessage();
              setTimeout(() => {
                setOpen(false);
                navigate("/setting/group");
              }, 2000);
            },
            onError: (error) => {
              showErrorMessage(error);
            },
          })
        );
      }
    }
  };

  const handleCanceled = () => {
    navigate("/setting/group");
    setFormData({
      name: "",
      profileId: "",
      description: "",
      accesses: [],
      permissions: [],
      partner: null,
    });
    setSelectedPermission([]);
    dispatch(setSelectedAccess([]));
    setInternalCategory("");
    setPartner({
      acronym: "",
      concernedBranches: [],
      generalEmailAddress: "",
      interventionDomains: "",
      // interventionZone: "", // Champ désactivé (ne pas utiliser)
      legalStatuses: [],
      mainPhoneNumber: "",
      otherConcernedBranches: "",
      otherLegalStatus: "",
      otherPartnershipType: "",
      otherReferenceDocument: "",
      otherReportingChannel: "",
      organizationName: "",
      partnershipTypes: [],
      physicalOrPostalAddress: "",
      referenceDocuments: [],
      reportingChannels: [],
      rolesAndResponsibilities: "",
      website: "",
    });
    setIsExternalAgent(false);
    setErrors({
      name: "",
      description: "",
      accesses: "",
      permissions: "",
      access: "",
      internalCategory: "",
      partner: "",
      admin: "",
    });
  };

  const internalAgents = listAccess.filter((item) => item.internalAgent);
  const externalAccess = listAccess.filter(
    (item) => !item.internalAgent && !item.admin
  );
  const adminAccess = listAccess.filter((item) => item.admin);
  const isAdminChecked = adminAccess.some((a) => +a.id === selectedAccess[0]);

  React.useEffect(() => {
    const findAccess = listAccess.find((val) => +val.id === selectedAccess[0]);
    const isExternal = findAccess?.name.toLowerCase() === "agent externe";
    const selectedAdminOption = adminAccess.find(
      (agent) => +agent.id === selectedAccess[0]
    );
    const isSelectedAdminOption =  selectedAdminOption?.description.toLowerCase() === "admin";
    const paramPermissionIds = listPermission.filter((p) =>
        ["parametre", "paramètre"].includes(p.description.toLowerCase())
      )
      .map((p) => p.id);
    const externalPerms = listPermission
      .filter((p) => ["Plaintes", "Dashboard"].includes(p.description))
      .map((p) => p.id);

    setSelectedPermission((currentPermissions) => {
      let updatedPermissions = [...currentPermissions];
      if (isExternal) {
        updatedPermissions = updatedPermissions.filter((p) =>
          externalPerms.includes(p)
        );
        externalPerms.forEach((id) => {
          if (!updatedPermissions.includes(id)) {
            updatedPermissions.push(id);
          }
        });
      } else if (isSelectedAdminOption) {
        paramPermissionIds.forEach((id) => {
          if (!updatedPermissions.includes(id)) {
            updatedPermissions.push(id);
          }
        });
      } else {
        if (selectedAccess.length === 0) {
          updatedPermissions = [];
        } else {
          updatedPermissions = updatedPermissions.filter((p) => {
            const perm = listPermission.find((per) => per.id === p);
            return isEditMode ? perm : (
              perm?.description.toLowerCase() !== "parametre" &&
              perm?.description.toLowerCase() !== "paramètre"
            );
          });
        }
      }
      setFormData((prev) => ({
        ...prev,
        permissions: updatedPermissions,
        accesses: selectedAccess,
      }));
      return updatedPermissions;
    });
    //eslint-disable-next-line
  }, [selectedAccess, listPermission]);

  React.useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      permissions: "",
    }));
  }, [selectedPermission]);

  const handleChangePermission = (id: string) => {
    if (isExternalSelected) {
      const externalPerms = listPermission
        .filter((p) => ["Plaintes", "Dashboard"].includes(p.description))
        .map((p) => p.id);

      if (!externalPerms.includes(id)) {
        return;
      }
    }

    const permission = selectedPermission.includes(id)
      ? selectedPermission.filter((p) => p !== id)
      : [...selectedPermission, id];
    setSelectedPermission(permission);
    setFormData({ ...formData, permissions: permission });
  };

  return (
    <div className="container flex justify-content-center py-auto">
      <ExternalAgentForm
        isExternalAgent={isExternalAgent}
        setIsExternalAgent={setIsExternalAgent}
        setFormData={setPartner}
        formData={partner}
        setFile={setFile}
        isEditMode={isEditMode}
        resetFormAndErrors={() => {
          if (!isEditMode) {
            setPartner({
              acronym: "",
              concernedBranches: [],
              generalEmailAddress: "",
              interventionDomains: "",
              // interventionZone: "", // Champ désactivé (ne pas utiliser)
              legalStatuses: [],
              mainPhoneNumber: "",
              otherConcernedBranches: "",
              otherLegalStatus: "",
              otherPartnershipType: "",
              otherReferenceDocument: "",
              otherReportingChannel: "",
              organizationName: "",
              partnershipTypes: [],
              physicalOrPostalAddress: "",
              referenceDocuments: [],
              reportingChannels: [],
              rolesAndResponsibilities: "",
              website: "",
            });
            setSelectedPermission([]);
            dispatch(setSelectedAccess([]));
            setInternalCategory("");
            setErrors({
              name: "",
              description: "",
              accesses: "",
              permissions: "",
              access: "",
              internalCategory: "",
              partner: "",
              admin: "",
            });
          }
        }}
      />
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <div className="bg-white rounded-lg lg:w-1/2 w-full my-auto gap-[20px] column p-8 ">
        <div className={styles.title}>
          {idProfile ? "Modifier un groupe" : "Ajouter un groupe"}
        </div>
        {loadingProfilById === "loading" && idProfile ? (
          <div className="flex justify-center py-5">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="column flex flex-column gap-[3px] ">
              <div className="flex flex-col gap-3 mb-3">
                <div className="flex-1 gap-1">
                  <label>Nom du groupe (obligatoire)</label>
                  <InputText
                    name="nom"
                    className="w-full"
                    maxLength={255}
                    placeholder="Nom du groupe"
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (e.target.value !== "") {
                        setErrors({ ...errors, name: "" });
                      }
                    }}
                    value={formData.name}
                  />
                  {errors.name && (
                    <small className="text-red-600">{errors.name}</small>
                  )}
                </div>
                <div className="flex-1 gap-1">
                  <label>Description (obligatoire)</label>
                  <InputText
                    name="nom"
                    className="w-full"
                    maxLength={255}
                    placeholder="Description du groupe"
                    value={formData.description}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        setErrors({ ...errors, description: "" });
                      }
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      });
                    }}
                  />
                  {errors.description && (
                    <small className="text-red-600">{errors.description}</small>
                  )}
                </div>

                <div className="flex-1 gap-1">
                  <label>Permissions (obligatoire)</label>
                  {loadPermission === "loading" && !idProfile ? (
                    <div className="flex justify-center py-5">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {listPermission.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center ml-5 mt-3"
                        >
                          <Checkbox
                            checked={selectedPermission.includes(item.id)}
                            onChange={() => handleChangePermission(item.id)}
                            disabled={
                              (
                                // !isEditMode &&
                                item.description.toLowerCase() ===
                                  "parametre") ||
                              (isExternalSelected &&
                                item.description !== "Plaintes" &&
                                item.description !== "Dashboard")
                            }
                          />
                          <label
                            htmlFor={`${item.id}`}
                            className="ml-2"
                            style={{
                              color:
                                (
                                  // !isEditMode &&
                                  item.description.toLowerCase() ===
                                    "parametre") ||
                                (isExternalSelected &&
                                  item.description !== "Plaintes" &&
                                  item.description !== "Dashboard")
                                  ? "#a0a0a0"
                                  : "inherit",
                            }}
                          >
                            {item.description === "Parametre"
                              ? "Paramètre"
                              : item.description}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {errors.permissions && (
                  <small className="text-red-600">{errors.permissions}</small>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex-1">
                  <label className="font-medium text-sm mb-1 block">
                    Accès (Obligatoire)
                  </label>
                  {loadAccess === "loading" && !idProfile ? (
                    <div className="flex justify-center py-5">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-2 ml-5">
                        <div className="flex items-center gap-2 my-1">
                          <RadioButton
                            name="access"
                            value={100}
                            onChange={(e) => {
                              dispatch(setSelectedAccess([100]));
                            }}
                            checked={
                              selectedAccess[0] === 100 || isAdminChecked
                            }
                          />
                          <label htmlFor="access">Admin</label>
                        </div>
                        {(selectedAccess[0] === 100 || isAdminChecked) && (
                          <div>
                            {/* //liste derourlante pour choisir un admin */}
                            <Dropdown
                              value={selectedAccess[0].toString()}
                              options={adminAccess.map((agent) => ({
                                label: agent.description,
                                value: agent.id,
                              }))}
                              onChange={(e) => {
                                dispatch(setSelectedAccess([+e.value]));
                              }}
                              placeholder="Sélectionnez un admin"
                              className="w-full"
                              showClear
                            />
                            {errors.admin !== "" && (
                              <div className="text-red-600 text-sm mt-2">
                                {errors.admin}
                              </div>
                            )}
                          </div>
                        )}
                        {externalAccess.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-2 my-1"
                            onClick={()=> {
                              if(item.name.toLowerCase() === "agent externe"){
                                  setIsExternalAgent(true)
                                }
                            }}
                          >
                            <RadioButton
                              inputId={`access-${item.id}`}
                              name="access"
                              value={item.id}
                              onChange={() => {
                                 if(item.name.toLowerCase() === "agent externe"){
                                  setIsExternalAgent(true)
                                }
                                dispatch(setSelectedAccess([+item.id]));
                                setFormData((prev: any) => ({
                                  ...prev,
                                  accesses: [+item.id],
                                }));
                                setInternalCategory("");
                                setErrors((prevErrors: any) => ({
                                  ...prevErrors,
                                  accesses: "",
                                }));
                              }}
                              checked={selectedAccess[0] === +item.id}
                            />
                            <label htmlFor={`access-${item.id}`}>
                              {item.description}
                            </label>
                          </div>
                        ))}

                        {internalAgents.length > 0 && (
                          <div className="flex items-center gap-2">
                            <RadioButton
                              inputId="access-internal"
                              name="access"
                              value="0"
                              onChange={() => {
                                dispatch(setSelectedAccess([0]));
                                setFormData((prev: any) => ({
                                  ...prev,
                                  internalCategory: "",
                                }));
                                setInternalCategory("");
                                setErrors((prevErrors: any) => ({
                                  ...prevErrors,
                                  accesses: "",
                                  internalCategory: "",
                                }));
                              }}
                              checked={
                                selectedAccess[0] === 0 ||
                                internalAgents.some(
                                  (a) => selectedAccess[0] === +a.id
                                )
                              }
                              className={`${
                                internalCategory
                                  ? "bg-[#08f7f7]"
                                  : "bg-gray-200"
                              } rounded-full border-2 border-transparent hover:border-blue-600 transition-colors flex items-center justify-center p-3`}
                            />
                            <label htmlFor="access-internal">
                              Agent Interne
                            </label>
                          </div>
                        )}
                      </div>

                      {(selectedAccess.includes(0) ||
                        internalAgents.some(
                          (a) => +a.id === +selectedAccess[0]
                        )) && (
                        <div className="mt-3">
                          <Dropdown
                            value={internalCategory}
                            options={internalAgents.map((agent) => ({
                              label: agent.description,
                              value: agent.id,
                            }))}
                            onChange={(e) => {
                              const categoryId = e.value;
                              setInternalCategory(categoryId);

                              if (categoryId) {
                                dispatch(setSelectedAccess([categoryId]));
                                setFormData((prev: any) => ({
                                  ...prev,
                                  accesses: [categoryId],
                                }));
                                setErrors((prevErrors: any) => ({
                                  ...prevErrors,
                                  internalCategory: "",
                                }));
                              } else {
                                dispatch(setSelectedAccess([0]));
                                setFormData((prev: any) => ({
                                  ...prev,
                                  accesses: [0],
                                }));
                              }
                            }}
                            placeholder="Sélectionnez un agent interne"
                            className="w-full"
                            showClear
                          />
                          {errors.internalCategory && (
                            <div className="text-red-600 text-sm mt-2">
                              {errors.internalCategory}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {errors.accesses && (
                <small className="text-red-600">{errors.accesses}</small>
              )}
            </div>

            <div className="flex pt-4 gap-3 justify-content-center">
              <Button
                onClick={handleCanceled}
                className={classNames("btn btn-secondary w-40", {
                  "btn-secondary-disabled":
                    loading === FetchStatusType.LOADING ||
                    loadingEdit === FetchStatusType.LOADING,
                })}
                disabled={
                  loading === FetchStatusType.LOADING ||
                  loadingEdit === FetchStatusType.LOADING
                }
              >
                Annuler
              </Button>
              <Button
                onClick={handleSaveProfil}
                className={classNames("btn btn-primary w-40", {
                  "btn-primary-disabled":
                    loading === FetchStatusType.LOADING ||
                    loadingEdit === FetchStatusType.LOADING,
                })}
                disabled={
                  loading === FetchStatusType.LOADING ||
                  loadingEdit === FetchStatusType.LOADING
                }
              >
                {loading === FetchStatusType.LOADING ||
                  (loadingEdit === FetchStatusType.LOADING && (
                    <div className="mr-2 w-4 h-4 rounded-full animate-spin border-y-4 border-solid border-white border-t-transparent shadow-md pe-2"></div>
                  ))}
                Enregistrer
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddingProfile;
